// SmtpAccountDialog — диалог настройки SMTP-аккаунта (V15 «Почта» W7, ТЗ §3.8).
// PUT /api/email/settings/smtp {fromEmail, displayName?, smtpHost, smtpPort, smtpSecurity,
// username, password?} — upsert по (user_id, SMTP); password null/пустой → BE сохраняет
// прежний секрет (§3.5 канон hasSecret). Маска: при существующем пароле (hasSecret) —
// placeholder «••••••» + подсказка; DTO секреты не содержит (NFR-1, PINTEST-предусмотрительность).

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField } from '@mui/material';
import { emailApi, type EmailAccount, type SmtpSecurityType } from '@/api/email';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** Маска «пароль сохранён» (секреты в UI никогда не отображаются — TЗ §3.8 «• • •»). */
const SAVED_PASSWORD_MASK = '••••••';

/** Границы SMTP-порта (валидация M-7: 1..65535 — DDL CHECK V39 chk_email_accounts_smtp_fields). */
const SMTP_PORT_MIN = 1;
const SMTP_PORT_MAX = 65535;

/** Варианты безопасности SMTP (SSL | STARTTLS; PLAIN — вне MVP, запрет секретов §3.4). */
const SECURITY_OPTIONS: { value: SmtpSecurityType; labelKey: string }[] = [
  { value: 'SSL', labelKey: 'email.smtp.security.SSL' },
  { value: 'STARTTLS', labelKey: 'email.smtp.security.STARTTLS' },
];

interface SmtpAccountDialogProps {
  open: boolean;
  onClose: () => void;
  /** Редактируемый аккаунт (null/undefined — создание нового). */
  account?: EmailAccount | null;
}

/** Диалог настроек SMTP: форма + PUT-мутация (upsert; пустой пароль — прежний секрет). */
export function SmtpAccountDialog({ open, onClose, account = null }: SmtpAccountDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [fromEmail, setFromEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('');
  const [smtpSecurity, setSmtpSecurity] = useState<SmtpSecurityType>('SSL');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Сброс формы под аккаунт при каждом открытии (канон InvoiceEditDialog: password всегда пустой).
  useEffect(() => {
    if (!open) return;
    setFromEmail(account?.fromEmail ?? '');
    setDisplayName(account?.displayName ?? '');
    setSmtpHost(account?.smtpHost ?? '');
    setSmtpPort(account?.smtpPort != null ? String(account.smtpPort) : '');
    setSmtpSecurity(account?.smtpSecurity ?? 'SSL');
    setUsername(account?.username ?? '');
    setPassword('');
  }, [open, account]);

  const portNumber = Number.parseInt(smtpPort, 10);
  const portInvalid = smtpPort.trim() !== '' && (!Number.isFinite(portNumber) || portNumber < SMTP_PORT_MIN || portNumber > SMTP_PORT_MAX);
  const canSubmit = fromEmail.trim() !== '' && smtpHost.trim() !== '' && !portInvalid && username.trim() !== '';

  const saveMutation = useMutation({
    mutationFn: () =>
      emailApi.upsertSmtp({
        fromEmail: fromEmail.trim(),
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
        smtpHost: smtpHost.trim(),
        smtpPort: portNumber,
        smtpSecurity,
        username: username.trim(),
        // Пустой пароль — «не менять» (BE: прежний секрет; канон §3.5).
        ...(password.length > 0 ? { password } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['email'] });
      onClose();
    },
  });

  const isPending = saveMutation.isPending;
  const saveError = saveMutation.isError ? (
    <Alert severity="error">{extractApiErrorMessage(saveMutation.error, t('email.smtp.error'))}</Alert>
  ) : null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('email.smtp.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {saveError}
          <TextField
            label={t('email.smtp.fromEmail')}
            fullWidth
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            inputProps={{ 'aria-label': t('email.smtp.fromEmail') }}
          />
          <TextField
            label={t('email.smtp.displayName')}
            fullWidth
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            inputProps={{ 'aria-label': t('email.smtp.displayName') }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label={t('email.smtp.host')}
              fullWidth
              value={smtpHost}
              onChange={(e) => setSmtpHost(e.target.value)}
              inputProps={{ 'aria-label': t('email.smtp.host') }}
            />
            <TextField
              label={t('email.smtp.port')}
              value={smtpPort}
              error={portInvalid}
              onChange={(e) => setSmtpPort(e.target.value)}
              inputProps={{ 'aria-label': t('email.smtp.port') }}
            />
          </Stack>
          <TextField
            select
            label={t('email.smtp.security')}
            fullWidth
            value={smtpSecurity}
            onChange={(e) => setSmtpSecurity(e.target.value as SmtpSecurityType)}
            inputProps={{ 'aria-label': t('email.smtp.security') }}
          >
            {SECURITY_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('email.smtp.username')}
            fullWidth
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            inputProps={{ 'aria-label': t('email.smtp.username') }}
          />
          <TextField
            label={t('email.smtp.password')}
            fullWidth
            type="password"
            // Маска: пароль не отображается (NFR-1; при hasSecret — placeholder «••••••»).
            placeholder={account?.hasSecret ? SAVED_PASSWORD_MASK : ''}
            helperText={account?.hasSecret ? t('email.smtp.passwordHint') : undefined}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            inputProps={{ 'aria-label': t('email.smtp.password') }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => saveMutation.mutate()}
          disabled={isPending || !canSubmit}
        >
          {t('email.smtp.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
